'use client'

import * as ConfirmDialogPrimitive from '@radix-ui/react-dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import * as React from 'react'

import { cn } from '../../lib'
import { VStack } from '../layout'

const ConfirmDialog = ConfirmDialogPrimitive.Root

const ConfirmDialogTrigger = ConfirmDialogPrimitive.Trigger

const ConfirmDialogPortal = ConfirmDialogPrimitive.Portal

const ConfirmDialogOverlay = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-50 bg-[#1C1C1C]/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
))

const ConfirmDialogContent = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Content> & {
    onClose?: () => void
    isInternalDialog?: boolean
  }
>(({ className, onClose, isInternalDialog = true, children, ...props }, ref) => {
  return (
    <ConfirmDialogPortal>
      {isInternalDialog && <ConfirmDialogOverlay onClick={onClose} />}
      <ConfirmDialogPrimitive.Content
        ref={ref}
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 51,
          ...props.style,
        }}
        className={cn(
          'fixed bg-white z-[51] rounded-lg border shadow-lg',
          'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
          'grid w-[calc(100dvw-40px)] mobile:min-w-[360px] tablet:max-w-[480px] gap-8 p-5',
          [
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          ],
          className,
        )}
        {...props}
      >
        <VisuallyHidden.Root asChild>
          <ConfirmDialogPrimitive.Title>Dialog</ConfirmDialogPrimitive.Title>
        </VisuallyHidden.Root>
        {children}
      </ConfirmDialogPrimitive.Content>
    </ConfirmDialogPortal>
  )
})

const ConfirmDialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('space-y-[5px] gap-5', className)} {...props} />
)

const ConfirmDialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('flex-col-reverse', className)} {...props} />
)

const ConfirmDialogTitle = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Title ref={ref} className={cn('flex text-2xl font-semibold', className)} {...props} />
))

const ConfirmDialogDescription = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

const ConfirmDialogClose = React.forwardRef<
  React.ElementRef<typeof ConfirmDialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ConfirmDialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ConfirmDialogPrimitive.Close ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

ConfirmDialogOverlay.displayName = ConfirmDialogPrimitive.Overlay.displayName
ConfirmDialogContent.displayName = ConfirmDialogPrimitive.Content.displayName
ConfirmDialogHeader.displayName = 'ConfirmDialogHeader'
ConfirmDialogFooter.displayName = 'ConfirmDialogFooter'
ConfirmDialogTitle.displayName = ConfirmDialogPrimitive.Title.displayName
ConfirmDialogDescription.displayName = ConfirmDialogPrimitive.Description.displayName
ConfirmDialogClose.displayName = ConfirmDialogPrimitive.Close.displayName

export {
  ConfirmDialog,
  ConfirmDialogClose,
  ConfirmDialogContent,
  ConfirmDialogDescription,
  ConfirmDialogFooter,
  ConfirmDialogHeader,
  ConfirmDialogOverlay,
  ConfirmDialogPortal,
  ConfirmDialogTitle,
  ConfirmDialogTrigger,
}
